/**
 * Canonical select primitive — same API as <Input> but renders a
 * <select> with an options array. See Input.tsx for the prop docs.
 *
 *   <Select label="Status" value={s} onChange={…} options={[
 *     { value: "active", label: "Active" },
 *     { value: "draft",  label: "Draft" },
 *   ]} />
 *
 * For complex / searchable selects, drop down to the existing
 * `react-select` (or whatever) — this primitive is for the common case.
 */

import React, { useId } from "react";
import { useTheme } from "../../context/ThemeContext";
import { getInputStyles } from "../../utils/themeUtils";
import { TOKENS, type InputSize } from "../../utils/designTokens";
import Label from "./Label";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /**
   * Renders the option inside an <optgroup> with this label. Consecutive
   * options with the same group share one <optgroup>; options without a group
   * render bare, in order.
   */
  group?: string;
}

const renderOptions = (options: SelectOption[]): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  let i = 0;
  while (i < options.length) {
    const group = options[i].group;
    if (!group) {
      const opt = options[i];
      out.push(
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </option>
      );
      i += 1;
      continue;
    }
    const members: SelectOption[] = [];
    while (i < options.length && options[i].group === group) {
      members.push(options[i]);
      i += 1;
    }
    out.push(
      <optgroup key={`group-${group}-${i}`} label={group}>
        {members.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </optgroup>
    );
  }
  return out;
};

interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: React.ReactNode;
  size?: InputSize;
  error?: string;
  hint?: string;
  requiredMarker?: boolean;
  options: SelectOption[];
  /** Optional placeholder rendered as a disabled first option. */
  placeholder?: string;
  /**
   * Classes for the WRAPPER div, not the control. `className` reaches the
   * control only, so a grid span or a width constraint had no route in — two
   * separate sweeps wrapped the primitive in an extra div to get one.
   */
  wrapperClassName?: string;
  /**
   * Emit `w-full` on the wrapper. Defaults to true.
   *
   * Must be a prop rather than something `wrapperClassName` overrides, for the
   * same reason Card's `background` is: Tailwind resolves same-specificity
   * utilities by stylesheet SOURCE order, and `.w-full` is emitted AFTER the
   * numeric widths (measured in the built CSS: w-32 @33635, w-64 @33813,
   * w-full @34175). So a caller passing `w-32` would still render full width.
   */
  fullWidth?: boolean;
  /**
   * Icon rendered inside the control at its leading edge; the control gains
   * pl-10 to make room.
   *
   * This has to live in the primitive. The icon is absolutely positioned
   * against a relative box wrapping the control, and `className` reaches the
   * control itself — a caller has no way to introduce that box. 56 such
   * adornments across 39 files are hand-rolled for exactly this reason, and
   * each one keeps its page off the primitive.
   *
   * pl-10 does beat the size bucket's px-*: .pl-10 is emitted at byte 82561 of
   * the built stylesheet, after .px-3 (81369) and .px-4 (81416). Measured, not
   * assumed — if it lost, the icon would sit on top of the text.
   */
  leadingIcon?: React.ReactNode;
}

const Select: React.FC<SelectProps> = ({
  label,
  size = "md",
  error,
  hint,
  requiredMarker,
  required,
  options,
  placeholder,
  id: providedId,
  className = "",
  wrapperClassName = "",
  fullWidth = true,
  leadingIcon,
  value,
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  const generatedId = useId();
  const id = providedId || generatedId;

  const classes = [
    TOKENS.input.base,
    TOKENS.input.sizes[size],
    getInputStyles(isDarkMode),
    error ? "border-red-500 focus:ring-red-500" : "",
    leadingIcon ? "pl-10" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const wrapperClasses = [fullWidth ? "w-full" : "", wrapperClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClasses}>
      {label && (
        <Label htmlFor={id} required={requiredMarker || required}>
          {label}
        </Label>
      )}
      {/* The positioning box is rendered ONLY when there is an icon.
          Wrapping unconditionally would insert a div into the DOM of every
          Input and Select in the app — which broke a test asserting on the
          wrapper via parentElement, and would silently have broken any
          caller-side CSS relying on the existing structure.

          aria-hidden + pointer-events-none: the icon is decoration, and a
          click on it must reach the control underneath. */}
      {leadingIcon ? (
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex items-center text-gray-400 dark:text-gray-500"
          >
            {leadingIcon}
          </span>
        <select
          id={id}
          required={required}
          className={classes}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {renderOptions(options)}
        </select>
        </div>
      ) : (
      <select
        id={id}
        required={required}
        className={classes}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {renderOptions(options)}
      </select>
      )}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
};

export default Select;
